import React, { Children, ComponentType, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { OverviewEngineFactories } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import {
	ComponentMap,
	DefaultComponentMap
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/component-map";
import {
	DefaultWidgetMap,
	WidgetMap
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/widget-map";
import { EnumerationFilterOptionsView } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/filters/options-views/enumeration-filter-options-view";
import { FilterProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/faceted-search";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";

import { isAdmin, isUser, selectStructuredRoles } from "../../../utilities/user";
import { convertAppIdToKey } from "../../../utilities/applicationKeyUtils";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { CustomFormatReferenceCell } from "../../../component/CustomFormatReferenceCell";
import { ExternalEnumOverviewEngine } from "../../../component/externalEnumOverviewEngine";
import { createOpenTaskDocumentPayloadFromProcessInstanceIdV2 } from "../../../utilities/processInstanceHelpers";
import { openTaskDocument } from "../../processOverview/store/actions";
import {
	isWiedervorlageProcessDocument,
	isWiedervorlageProcessOverviewData,
	WiedervorlageProcessDocument
} from "../../wiedervorlagen/types";
import { PATH_TO_STATUS } from "../../../utilities/variables/documentModelPaths";
import { GeneralOverviewWidgetsMap } from "../../generalOverviewEngine/GeneralOverviewEngine";

import EnumerationOption = EnumerationFilterOptionsView.EnumerationOption;

type UpcomingDeadlinesActivityData = {
	enumerationStatusOptions?: EnumerationFilterOptionsView.Props["enumerationOptions"];
};

export function UpcomingDeadlinesOverviewEngine(props: OverviewEngineFactories.ViewComponentProps): React.ReactElement {
	const { activityId } = props;

	const data = useSelector(ActivitySelectors.data(activityId));
	const dispatch = useDispatch();

	const [documents, setDocuments] = React.useState<WiedervorlageProcessDocument[]>([]);

	React.useEffect(() => {
		if (!data || !isWiedervorlageProcessOverviewData(data)) {
			return;
		}
		setDocuments(data.documents);
	}, [data]);

	const eventHandlers = useMemo(
		() => ({
			async onRowClick(params: { documentId: string; customEvent?: string }) {
				const taskDocument = getDocumentById(documents, params.documentId);
				if (!isWiedervorlageProcessDocument(taskDocument)) {
					return;
				}

				if (!taskDocument.Process) {
					return;
				}

				const payload = await createOpenTaskDocumentPayloadFromProcessInstanceIdV2(
					taskDocument.Process.processMetadata.processId,
					taskDocument.Process.processMetadata.fachverfahren.id + "_Prozess_WF",
					activityId
				);

				if (!payload) {
					return;
				}

				dispatch(openTaskDocument(payload));
			}
		}),
		[dispatch, activityId, documents]
	);

	return (
		<ActivityIdContext.Provider value={activityId}>
			<ExternalEnumOverviewEngine
				componentMap={customComponentMap}
				eventHandlers={eventHandlers}
				widgetMap={widgetsMap}
				{...props}
			></ExternalEnumOverviewEngine>
		</ActivityIdContext.Provider>
	);
}

const FV_REGEX = /\s*\[[^\]]*]/g;

export function filterUserFachverfahren(enumerationOptions: EnumerationOption[], user: unknown): EnumerationOption[] {
	if (isUser(user) && isAdmin(user)) {
		return enumerationOptions;
	}
	const userFvKeys = isUser(user) ? new Set(selectStructuredRoles(user).map(({ key }) => key)) : new Set<string>();

	return enumerationOptions.filter(option => {
		const fachverfahrenMarker = option.label.match(/\[([^\]]+)\]\s*$/)?.[1];
		const optionFachverfahrenKey = convertAppIdToKey((fachverfahrenMarker ?? option.value).split("_")[0]);
		const matchesUserFachverfahren = userFvKeys.has(optionFachverfahrenKey);
		const hasNoBracketedSuffix = option.label.search(FV_REGEX) === -1;
		return matchesUserFachverfahren || hasNoBracketedSuffix;
	});
}

function getDocumentById(
	documents: WiedervorlageProcessDocument[],
	id: string
): WiedervorlageProcessDocument | undefined {
	return documents.find(doc => doc.id === id);
}

export function sanitizeEnumerationOptions(
	enumerationOptions: EnumerationFilterOptionsView.Props["enumerationOptions"]
): EnumerationFilterOptionsView.Props["enumerationOptions"] {
	const indexByLabel = new Map<string, number>();

	return enumerationOptions.reduce<EnumerationFilterOptionsView.Props["enumerationOptions"]>((result, option) => {
		const sanitizedLabel = option.label.replace(FV_REGEX, "").trim();
		const existingIndex = indexByLabel.get(sanitizedLabel);

		if (existingIndex === undefined) {
			indexByLabel.set(sanitizedLabel, result.length);
			result.push({ ...option, label: sanitizedLabel });
			return result;
		}

		// Keep the selected variant so its state is shown and deselecting toggles the selected value.
		const existing = result[existingIndex];
		if ((option.checked && !existing.checked) || (option.active && !existing.active && !existing.checked)) {
			result[existingIndex] = { ...option, label: sanitizedLabel };
		}
		return result;
	}, []);
}

const CustomUpcomingDeadlinesEnumerationFilterOptionsView: ComponentType<EnumerationFilterOptionsView.Props> =
	function (props) {
		const { path, enumerationOptions } = props;
		const activityId = React.useContext(ActivityIdContext);
		const dispatch = useDispatch();
		const activityData = useSelector(ActivitySelectors.data(activityId ?? ""));
		const isStatusFilter = path.at(-1)?.elementName === PATH_TO_STATUS.at(-1)?.elementName;
		const user = useSelector(UaaSelectors.user);
		const sanitizedEnumerationOptions = useMemo(
			() =>
				isStatusFilter
					? sanitizeEnumerationOptions(filterUserFachverfahren(enumerationOptions, user)).sort((a, b) =>
							a.label.localeCompare(b.label, undefined, { numeric: true })
						)
					: enumerationOptions,
			[enumerationOptions, isStatusFilter, user]
		);

		useEffect(() => {
			if (!activityId || !isStatusFilter) {
				return;
			}

			const existingActivityData: UpcomingDeadlinesActivityData =
				typeof activityData === "object" && activityData !== null
					? (activityData as UpcomingDeadlinesActivityData)
					: {};

			const existingOptions = existingActivityData.enumerationStatusOptions;
			// A12 recreates option arrays on store updates; only lookup changes may trigger another write.
			if (
				existingOptions &&
				existingOptions.length === enumerationOptions.length &&
				existingOptions.every(
					(option, index) =>
						option.value === enumerationOptions[index].value &&
						option.label === enumerationOptions[index].label
				)
			) {
				return;
			}
			dispatch(
				ActivityActions.setData({
					activityId,
					data: {
						...existingActivityData,
						enumerationStatusOptions: enumerationOptions
					}
				})
			);
		}, [activityData, activityId, dispatch, enumerationOptions, isStatusFilter]);

		return (
			<DefaultComponentMap.FilterOptionsViews.EnumerationFilterOptionsView
				{...props}
				enumerationOptions={sanitizedEnumerationOptions}
			/>
		);
	};

const PATH_STATUS = "/Process/genehmigungsverfahren/prozessschritte/Status";
export const CustomUpcomingDeadlinesFilter: ComponentType<FilterProps> = function CustomUpcomingDeadlinesFilter(props) {
	const { options, id } = props;
	const statusOptions = useMemo(() => {
		if (!options || PATH_STATUS !== id) {
			return options;
		}

		const normalizedOptions = Children.toArray(options).flatMap(option => {
			if (typeof option === "string") {
				return [option];
			}

			if (typeof option === "object" && option && "props" in option) {
				const element = option as React.ReactElement<{ children?: React.ReactNode }>;
				return Children.toArray(element.props.children).filter(
					(child): child is string => typeof child === "string"
				);
			}

			return [];
		});

		return normalizedOptions
			.map(option => {
				if (typeof option !== "string") {
					return option;
				}

				return option
					.split(",")
					.map(item => item.replace(/\[[a-zA-Z0-9_]+]$/, ""))
					.join(",");
			})
			.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
	}, [options, id]);

	if (PATH_STATUS === id) {
		return <DefaultWidgetMap.Filter {...props} options={statusOptions} />;
	}

	return <DefaultWidgetMap.Filter {...props} />;
};

const widgetsMap: WidgetMap = {
	...GeneralOverviewWidgetsMap,
	Filter: CustomUpcomingDeadlinesFilter
};

const customComponentMap: ComponentMap = {
	...DefaultComponentMap,
	FilterOptionsViews: {
		...DefaultComponentMap.FilterOptionsViews,
		EnumerationFilterOptionsView: CustomUpcomingDeadlinesEnumerationFilterOptionsView
	},
	ReferenceCell: CustomFormatReferenceCell
};
